// ============================================================================
// FSA ENGAGEMENT SCREEN  (FSA-2.1)
// ============================================================================
// Multi-tab workspace shell for the per-engagement FSA flow. In FSA-2.1 only
// the **General / Basic** tab has working content — Settings card, Trial
// Balance card, Unmapped Ledger queue. Other workspace tabs (BS / P&L / CF /
// Notes / Other Reports) are placeholders showing "Available in FSA-2.2".
//
// Engagement context arrives via the module-navigation bridge: the basic-
// tier engagement screen calls openModule('fsa-engagement', { engagementId })
// and we read the id on mount via getModuleParams.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { getModuleParams, openModule } from 'services/moduleNavigation';
import { useFsaEngagementSettings } from 'hooks/api/fsa/useFsaEngagementSettings';
import { useCurrentTrialBalance } from 'hooks/api/fsa/useFsaTrialBalance';
import { useCoverage } from 'hooks/api/fsa/useFsaClassification';
import type { ID } from 'api/types';
import FsaEngagementSettingsModal from 'components/modals/modules/fsa/FsaEngagementSettingsModal';
import FsaRollForwardConfirmModal from 'components/modals/modules/fsa/FsaRollForwardConfirmModal';
import TrialBalanceUploadModal from 'components/modals/modules/fsa/TrialBalanceUploadModal';
import SourceTbRegistryModal from 'components/modals/modules/fsa/SourceTbRegistryModal';
import FsaOpeningBalanceReconciliationCard from './general/FsaOpeningBalanceReconciliationCard';
import { fsaTrialBalanceService } from 'api/services/fsa/fsaTrialBalanceService';
import type { ControlAccountSummary } from 'api/types/fsa/trialBalance.types';
// FSA-2.4 W1 — FsaUnmappedLedgerPopup removed from Workspace. Classification
// workflow now lives in the TB tab via the Unmapped chip + row actions.
// Component file kept on disk for potential future reuse.
import FsaConsolidatedPrintScreen from './FsaConsolidatedPrintScreen';
import FsaTrialBalanceTab from './FsaTrialBalanceTab';
import FsaNotesTab from './FsaNotesTab';
import FsaAdjustmentsTab from './FsaAdjustmentsTab';
import FsaReportsTab from './FsaReportsTab';
import FsaStatementScreen from './FsaStatementScreen';
import { useFsaStatementList } from 'hooks/api/fsa/useFsaStatements';
import { useFsaNotesList } from 'hooks/api/fsa/useFsaNotes';
import FsaSignaturePanelPane from './signature-panel/FsaSignaturePanelPane';

type WorkspaceTab = 'general' | 'trial-balance' | 'adjustments' | 'bs' | 'pl' | 'cf' | 'notes' | 'other' | 'signature-panel' | 'print-fs';

interface Props {
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const FsaEngagementScreen: React.FC<Props> = ({ tabId, moduleName, allowedActions = [] }) => {
  // Engagement id pulled from the module-navigation bridge (sessionStorage).
  // Each per-engagement tab stores its payload under the COMPOSITE tab id
  // (`fsa-engagement:<engagementId>`), so we read by the tabId prop. Falls
  // back to the legacy singleton key when tabId is unavailable (defensive —
  // every modern call path supplies tabId).
  const [engagementId, setEngagementId] = useState<ID | null>(null);
  useEffect(() => {
    const params = getModuleParams<{ engagementId?: ID }>(tabId ?? 'fsa-engagement');
    setEngagementId(params?.engagementId ?? null);
  }, [tabId]);

  const [activeTab, setActiveTab] = useState<WorkspaceTab>('general');
  const [downloadingWp, setDownloadingWp] = useState(false);
  // FSA-2.1.8 — once the user has visited the Trial Balance tab we keep its
  // component instance mounted (display:none toggle) even when they navigate
  // to another sub-tab. Reason: that tab owns the Edit Classification modal +
  // its minimize-to-bar closure refs; unmount/remount breaks restore because
  // the registered onRestore callback would be a closure over a stale
  // component instance. Same trick PRIMA's main layout uses for top-level
  // tab persistence.
  const [tbTabMounted, setTbTabMounted] = useState(false);
  useEffect(() => {
    if (activeTab === 'trial-balance' && !tbTabMounted) {
      setTbTabMounted(true);
    }
  }, [activeTab, tbTabMounted]);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  // FSA-2.3 — first-year CTA pre-selects OPENING when launching the upload
  // modal. Cleared on close so the next regular upload flow defaults to
  // CURRENT (the auditor can still flip the selector inside the modal).
  const [uploadInitialPurpose, setUploadInitialPurpose] = useState<'CURRENT' | 'OPENING'>('CURRENT');
  // FSA-2.3 Roll Forward — confirm dialog for "Create Next Year Engagement".
  const [rollForwardModalOpen, setRollForwardModalOpen] = useState(false);
  // FSA-2.4 W1 — unmappedPopupOpen state retired (popup removed; workflow
  // moved to TB tab). Kept the comment as a breadcrumb for future readers.
  // FSA-2.1.6 — source registry panel + control-account widget state.
  const [sourcesModalOpen, setSourcesModalOpen] = useState(false);
  const [controlSummary, setControlSummary] = useState<ControlAccountSummary | null>(null);

  // FSA-2.4 UI polish — instant tooltip for collapsed rail items.
  // Native `title` is delayed ~1s and easy to miss; this custom tooltip
  // fires on mouseenter, renders via portal so it escapes the rail's
  // overflow:hidden, and sits flush against the icon on the right.
  // Group label is included alongside the item label so the auditor
  // also picks up the section context.
  const [railTip, setRailTip] = useState<{ label: string; group: string; top: number; left: number } | null>(null);

  // Left-rail collapse — persisted across sessions. Defaults expanded.
  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try { return window.localStorage.getItem(RAIL_COLLAPSE_PREF) === 'true'; }
    catch { return false; }
  });
  const toggleRail = useCallback(() => {
    setRailCollapsed(prev => {
      const next = !prev;
      try { window.localStorage.setItem(RAIL_COLLAPSE_PREF, String(next)); } catch { /* noop */ }
      return next;
    });
  }, []);

  const {
    data: settings,
    loading: settingsLoading,
    refetch: refetchSettings,
  } = useFsaEngagementSettings(engagementId);
  const {
    data: currentTb,
    loading: tbLoading,
    refetch: refetchTb,
  } = useCurrentTrialBalance(engagementId);
  const { data: coverage, refetch: refetchCoverage } = useCoverage(currentTb?.id ?? null);

  // FSA-2.4 W2 — Notes + Statements progress strips on the Workspace
  // home tab. Both lists are paginated server-side elsewhere, but
  // these hooks return the full per-engagement set (~30-40 notes,
  // 5-8 statements) for at-a-glance status aggregation. Re-used in
  // each strip's compose step; loading state degrades gracefully.
  const { data: notesList } = useFsaNotesList(engagementId);
  const { data: statementsList } = useFsaStatementList(engagementId);

  // Notes status summary — buckets by lifecycle code.
  const notesSummary = useMemo(() => {
    const active = (notesList ?? []).filter(n => !n.isExcluded);
    const finalised = active.filter(n => (n.statusCode ?? '').toUpperCase() === 'APPROVED').length;
    const inReview  = active.filter(n => (n.statusCode ?? '').toUpperCase() === 'SUBMITTED').length;
    const draft     = active.filter(n =>
      (n.statusCode ?? '').toUpperCase() === 'DRAFT' && n.hasSavedData).length;
    const notStarted = active.filter(n => !n.hasSavedData).length;
    const excluded  = (notesList ?? []).filter(n => n.isExcluded).length;
    return {
      total: active.length,
      finalised,
      inReview,
      draft,
      notStarted,
      excluded,
      pctFinalised: active.length === 0 ? 0 : Math.round((finalised / active.length) * 100),
    };
  }, [notesList]);

  // Per-statement status (BS / PL / CF + any other FS-type reports).
  const statementsSummary = useMemo(() => {
    return (statementsList ?? [])
      .filter(s => (s.reportType ?? '').toUpperCase() === 'FINANCIAL_STATEMENT');
  }, [statementsList]);

  // FSA-2.4 W3 — exception strip. Aggregates the actionable issues
  // that need the auditor's attention: TB imbalance, unmapped ledgers
  // above a threshold, control accounts not netting to zero, BS-side
  // imbalance, statements not yet generated. Each item carries a
  // jump-to-tab CTA so the auditor can act in one click. Returns []
  // when nothing's wrong — the strip auto-hides in that case.
  const exceptions = useMemo(() => {
    const out: Array<{
      id: string;
      severity: 'error' | 'warning';
      label: string;
      detail?: string;
      onAction?: () => void;
      actionLabel?: string;
    }> = [];

    if (currentTb && Math.abs(currentTb.debitTotal - currentTb.creditTotal) > 0.005) {
      out.push({
        id: 'tb-imbalance',
        severity: 'error',
        label: 'Trial Balance does not tally',
        detail: `Dr − Cr = ${formatAmount(currentTb.debitTotal - currentTb.creditTotal)}`,
        onAction: () => setActiveTab('trial-balance'),
        actionLabel: 'Open TB',
      });
    }

    // Unmapped ledger count above the rule-of-thumb threshold (10% of
    // total, capped at 25 ledgers — beyond this the auditor really
    // needs to engage with classification before moving on).
    if (coverage && coverage.unclassified > 0 && coverage.total > 0) {
      const pct = (coverage.unclassified / coverage.total) * 100;
      if (pct > 10 || coverage.unclassified > 25) {
        out.push({
          id: 'unmapped',
          severity: 'warning',
          label: `${coverage.unclassified} ledgers unmapped`,
          detail: `${pct.toFixed(0)}% of total — classify before generating statements`,
          onAction: () => setActiveTab('trial-balance'),
          actionLabel: 'Open TB',
        });
      }
    }

    if (coverage && coverage.scheduleOnly > 0) {
      out.push({
        id: 'pending-class-split',
        severity: 'warning',
        label: `${coverage.scheduleOnly} ledgers pending class split`,
        detail: 'Mapped to a Note but leaf class deferred',
        onAction: () => setActiveTab('trial-balance'),
        actionLabel: 'Open TB',
      });
    }

    if (controlSummary && controlSummary.flaggedCount > 0 && !controlSummary.isBalanced) {
      out.push({
        id: 'control-accounts',
        severity: 'error',
        label: `Control accounts do not net to zero (${formatAmount(controlSummary.netTotal)})`,
        detail: `${controlSummary.flaggedCount} flagged entries — review the inter-branch / suspense balances`,
        onAction: () => setActiveTab('trial-balance'),
        actionLabel: 'Open TB',
      });
    }

    // Statements not yet generated — only flag once classification is
    // mostly done, so the strip doesn't nag during the initial
    // mapping phase.
    if (coverage && coverage.percentClassified >= 90 && statementsSummary.length > 0) {
      // CF defaults to Not Applicable (the auditor opts in), so an
      // un-generated CF is expected — don't nag. It still shows in the
      // Statements strip; the auditor generates it after Mark Applicable.
      const notGenerated = statementsSummary.filter(s => !s.isExcluded && !s.hasSavedData && s.reportCode !== 'CF');
      if (notGenerated.length > 0) {
        out.push({
          id: 'statements-not-generated',
          severity: 'warning',
          label: `${notGenerated.length} statement${notGenerated.length === 1 ? '' : 's'} not yet generated`,
          detail: notGenerated.map(s => s.reportCode).join(' · '),
        });
      }
    }

    // FSA-2.4 W3 — Balance Sheet drift: Total Assets ≠ Total Equity +
    // Liab on the saved BS. Same threshold as the BS screen's
    // reconciliation chip. Surfaces as an error since a non-tallied
    // BS blocks finalisation by definition.
    const bs = statementsSummary.find(s => s.reportCode === 'BS');
    if (bs && bs.hasSavedData && typeof bs.bsDriftAmount === 'number' && bs.bsDriftAmount > 0.005) {
      out.push({
        id: 'bs-drift',
        severity: 'error',
        label: `Balance Sheet does not reconcile`,
        detail: `Total Assets ≠ Total Equity + Liabilities (drift ${formatAmount(bs.bsDriftAmount)})`,
        onAction: () => setActiveTab('bs'),
        actionLabel: 'Open BS',
      });
    }

    return out;
  }, [currentTb, coverage, controlSummary, statementsSummary]);

  // FSA-2.2 Adjustments — cross-tab refresh signal. Bumped whenever a
  // JE is posted / reversed / saved / deleted in the Adjustments tab.
  // Forwarded to the Trial Balance tab so its Journal Net + Audited
  // columns refresh even though the tabs are sibling components.
  const [journalRefreshSignal, setJournalRefreshSignal] = useState(0);
  const bumpJournalRefresh = useCallback(() => {
    setJournalRefreshSignal((n) => n + 1);
  }, []);

  // FSA-2.1.6 — control-account summary widget. Refetches whenever the
  // consolidated TB id changes or the user flips a flag in the queue.
  const refetchControlSummary = useCallback(async () => {
    if (!currentTb?.id) { setControlSummary(null); return; }
    try {
      const r = await fsaTrialBalanceService.controlAccountSummary(currentTb.id);
      setControlSummary(r.data ?? null);
    } catch { /* widget is best-effort */ }
  }, [currentTb?.id]);
  useEffect(() => { refetchControlSummary(); }, [refetchControlSummary]);

  // FSA-2.3 — track whether the engagement has a live OPENING source TB.
  // Drives the first-year CTA on the General/Basic tab: when the
  // engagement has NO linked prior engagement AND no OPENING source,
  // surface a hint telling the auditor they can upload audited prior-
  // year closings as an OPENING TB to populate PY comparatives without
  // a full Roll Forward. Refetches whenever the consolidated TB
  // identity changes (proxy for "user just uploaded / removed a source").
  const [hasOpeningSource, setHasOpeningSource] = useState(false);
  const refetchHasOpening = useCallback(async () => {
    if (!settings || !engagementId) { setHasOpeningSource(false); return; }
    try {
      const r = await fsaTrialBalanceService.listSources(engagementId);
      const live = (r.data ?? []).filter(
        s => !s.isSuperseded && !s.isRemovedFromConsolidation,
      );
      setHasOpeningSource(live.some(s => s.purpose === 'OPENING'));
    } catch { /* best-effort */ }
  }, [engagementId, settings]);
  useEffect(() => { refetchHasOpening(); }, [refetchHasOpening, currentTb?.sourceCount]);

  const canEdit = allowedActions.includes('edit') || allowedActions.length === 0;

  // -------------------------------------------------------------------------
  // No engagement context guard
  // -------------------------------------------------------------------------
  if (!engagementId) {
    return (
      <div style={emptyStateStyle}>
        <IconSystem.alert size={32} />
        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>No engagement selected</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', textAlign: 'center', maxWidth: 420 }}>
          Open the FSA workspace from the Engagement Management screen by
          clicking the action menu on a row and selecting "Open FSA…".
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Tab strip
  // -------------------------------------------------------------------------
  return (
    <div className="module-master-container">
      {/* Header with engagement summary */}
      <div className="module-header" style={{ paddingBottom: '0.25rem' }}>
        <div className="module-header-content" style={{ width: '100%' }}>
          {/* 2-row header (PRIMA standard).
              Row 1: icon + client name (H1) | badges right-aligned.
              Row 2: single inline context line — "FSA Workspace · Type · Code · Description"
                     truncated to one line with ellipsis. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <IconSystem.chart size={20} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 className="module-title" style={{ margin: 0 }}>
                {settings?.clientName ?? 'FSA Workspace'}
              </h1>
            </div>
            {/* FSA-2.4 W1+ — header mini-status strip. Replaces the old
                metadata badges (FY / template / currency / rounding —
                all static, all duplicated in FSA Settings). Now shows
                the three at-a-glance dynamic signals the auditor checks
                first on every Workspace open: classification %, notes
                finalised, statements generated. Auto-shows when the
                engagement has TB context; minimised-modal strip still
                lives inside each sub-tab's own toolbar.

                Header chip tooltips include the underlying counts;
                clicking each opens the relevant sub-tab. */}
            {settings && (
              <div style={badgeStripStyle}>
                {coverage && coverage.total > 0 && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('trial-balance')}
                    style={headerChipButtonStyle(
                      coverage.percentClassified >= 99.5 ? '#dcfce7' : coverage.percentClassified >= 80 ? '#dbeafe' : '#fef3c7',
                      coverage.percentClassified >= 99.5 ? '#166534' : coverage.percentClassified >= 80 ? '#1e40af' : '#92400e',
                    )}
                    title={`${coverage.classified} of ${coverage.total} ledgers classified · ${coverage.unclassified} unmapped${coverage.scheduleOnly > 0 ? ` · ${coverage.scheduleOnly} pending class split` : ''}`}
                  >
                    📊 {coverage.percentClassified.toFixed(0)}% classified
                    {coverage.unclassified > 0 && <> · {coverage.unclassified} unmapped</>}
                  </button>
                )}
                {notesSummary.total > 0 && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('notes')}
                    style={headerChipButtonStyle(
                      notesSummary.pctFinalised === 100 ? '#dcfce7' : '#dbeafe',
                      notesSummary.pctFinalised === 100 ? '#166534' : '#1e40af',
                    )}
                    title={`${notesSummary.finalised} finalised · ${notesSummary.inReview} in review · ${notesSummary.draft} draft · ${notesSummary.notStarted} not started`}
                  >
                    📝 {notesSummary.finalised} of {notesSummary.total} notes finalised
                  </button>
                )}
                {statementsSummary.length > 0 && (() => {
                  // CF defaults to Not Applicable — treat an un-generated CF as
                  // NA (excluded) so it doesn't read as a pending statement.
                  // Once the auditor opts in (Mark Applicable / saves data) it
                  // has saved data and counts as active again.
                  const active = statementsSummary.filter(s => !s.isExcluded && !(s.reportCode === 'CF' && !s.hasSavedData));
                  const generated = active.filter(s => s.hasSavedData).length;
                  const notGenerated = active
                    .filter(s => !s.hasSavedData)
                    .map(s => s.reportCode);
                  const allGenerated = generated === active.length;
                  return (
                    <button
                      type="button"
                      onClick={() => setActiveTab('bs')}
                      style={headerChipButtonStyle(
                        allGenerated ? '#dcfce7' : '#fef3c7',
                        allGenerated ? '#166534' : '#92400e',
                      )}
                      title={allGenerated
                        ? `All ${active.length} statements generated`
                        : `${notGenerated.length} statement${notGenerated.length === 1 ? '' : 's'} pending: ${notGenerated.join(' · ')}`}
                    >
                      📑 {allGenerated
                        ? `${generated} of ${active.length} statements generated`
                        : `${notGenerated.join('/')} not yet generated`}
                    </button>
                  );
                })()}
              </div>
            )}
          </div>
          <div style={contextLineStyle} title={contextTitleText(settings)}>
            <span style={{ fontWeight: 600 }}>FSA Workspace</span>
            {settings?.engagementTypeName && (
              <>
                <span style={contextSeparatorStyle}>·</span>
                <span>{settings.engagementTypeName}</span>
              </>
            )}
            {settings?.engagementCode && (
              <>
                <span style={contextSeparatorStyle}>·</span>
                <span>{settings.engagementCode}</span>
              </>
            )}
            {settings?.engagementDescription && (
              <>
                <span style={contextSeparatorStyle}>·</span>
                <span style={{ color: 'var(--text-secondary)' }}>{settings.engagementDescription}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="module-content" style={{ marginTop: '6px' }}>
        {/* Workspace shell: left rail + content panel. The rail is grouped
            (Workspace / Statements / Reports) and collapses to icon-only mode
            on user toggle (persisted to localStorage). */}
        <div style={workspaceShellStyle}>
          {/* ---- LEFT RAIL ---- */}
          <aside style={railCollapsed ? railCollapsedStyle : railExpandedStyle}>
            <div style={railHeaderStyle}>
              {!railCollapsed && <span style={railHeaderTitleStyle}>WORKSPACE</span>}
              <button
                type="button"
                onClick={toggleRail}
                style={railToggleBtnStyle}
                title={railCollapsed ? 'Expand workspace menu' : 'Collapse workspace menu'}
                aria-label={railCollapsed ? 'Expand workspace menu' : 'Collapse workspace menu'}
              >
                {railCollapsed
                  ? <IconSystem.chevronRight size={14} />
                  : <IconSystem.chevronLeft size={14} />}
              </button>
            </div>
            <nav style={railNavStyle}>
              {WORKSPACE_GROUPS.map(group => (
                <div key={group.label} style={railGroupStyle}>
                  {!railCollapsed && (
                    <div style={railGroupHeaderStyle}>{group.label}</div>
                  )}
                  {group.items.map(item => {
                    const isActive  = activeTab === item.id;
                    const isDisabled = !settings && item.id !== 'general';
                    const itemStyle: React.CSSProperties = {
                      ...railItemStyle,
                      ...(railCollapsed ? railItemCollapsedStyle : {}),
                      ...(isActive ? railItemActiveStyle : {}),
                      ...(isDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
                    };
                    // Tooltip only when collapsed — when expanded the label
                    // is visible inline, so the popup would be redundant.
                    // Single handler covers MouseEnter + Focus (both expose
                    // currentTarget on HTMLButtonElement).
                    const showTip = railCollapsed
                      ? (e: React.SyntheticEvent<HTMLButtonElement>) => {
                          const r = e.currentTarget.getBoundingClientRect();
                          setRailTip({
                            label: item.label,
                            group: group.label,
                            top: r.top + r.height / 2,
                            left: r.right + 8,
                          });
                        }
                      : undefined;
                    const hideTip = railCollapsed ? () => setRailTip(null) : undefined;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => !isDisabled && setActiveTab(item.id)}
                        disabled={isDisabled}
                        style={itemStyle}
                        // Accessible label for screen readers; visible
                        // tooltip is the portal popup below.
                        aria-label={railCollapsed ? `${group.label}: ${item.label}` : item.label}
                        onMouseEnter={showTip}
                        onMouseLeave={hideTip}
                        onFocus={showTip}
                        onBlur={hideTip}
                      >
                        <span style={railItemIconStyle}>
                          <RailIcon iconKey={item.iconKey} size={16} />
                        </span>
                        {!railCollapsed && (
                          <span style={railItemLabelStyle}>{item.label}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </nav>
            {/* FSA-2.4 UI polish — portal-rendered rail tooltip. Position
                is computed in the button's onMouseEnter from
                getBoundingClientRect; vertical-center on the icon. */}
            {railTip && createPortal(
              <div
                role="tooltip"
                style={{
                  ...railTooltipStyle,
                  top: railTip.top,
                  left: railTip.left,
                }}
              >
                <div style={railTooltipGroupStyle}>{railTip.group}</div>
                <div style={railTooltipLabelStyle}>{railTip.label}</div>
              </div>,
              document.body
            )}
          </aside>

          {/* ---- CONTENT PANEL ---- */}
          <div style={contentPanelStyle}>
        {activeTab === 'general' && (
          <div style={{ padding: '0.5rem 1.25rem 1.25rem', overflowY: 'auto', flex: 1 }}>
            {/* ---- Engagement actions strip (FSA-2.4 W3) ----
                 Engagement-lifecycle CTAs (today: Create Next Year /
                 Roll Forward; future: Submit for Client Approval,
                 Issue FS, etc. as FSA-2.3 lands). Lives at the top of
                 the Workspace, not buried inside a metadata card. */}
            {canEdit && settings && (
              <section style={{ ...cardStyle, marginBottom: 8 }}>
                <div style={compactRowStyle}>
                  <div style={compactRowMainStyle}>
                    <strong>Engagement actions</strong>
                    <span style={compactRowSeparatorStyle}>·</span>
                    <span style={{ color: 'var(--text-secondary)' }}>
                      Next-year roll-forward and finalisation actions
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={downloadingWp || !engagementId}
                      onClick={async () => {
                        if (!engagementId) return;
                        setDownloadingWp(true);
                        try {
                          await fsaTrialBalanceService.downloadNoteReconciliationReport(engagementId);
                        } catch {
                          notificationService.toast.error('Could not generate the working paper report.');
                        } finally {
                          setDownloadingWp(false);
                        }
                      }}
                      title="Download the Note Reconciliation & Ledger Breakup working paper (Excel) — saved vs derived totals, drift, and the ledger breakup behind each note"
                    >
                      <IconSystem.download size={14} /> {downloadingWp ? 'Preparing…' : 'Working Paper'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setRollForwardModalOpen(true)}
                      title="Roll forward this engagement to the next financial year"
                    >
                      <IconSystem.arrowRight size={14} /> Create Next Year
                    </button>
                  </div>
                </div>
              </section>
            )}

            {/* ---- Exception strip (FSA-2.4 W3) ----
                 Auto-hides when there's nothing to action. Each row
                 carries a severity colour and a one-click jump-to-tab
                 CTA. The strip is the auditor's "what needs my
                 attention" radar — sits at the top so it's the first
                 thing seen on every Workspace open. */}
            {exceptions.length > 0 && (
              <section style={{ ...cardStyle, marginBottom: 8, borderLeft: '3px solid #d97706' }}>
                <div style={cardHeaderStyle}>
                  <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <IconSystem.alert size={16} /> Needs attention
                  </h2>
                  <span style={pillStyle('#fef3c7', '#92400e')}>
                    {exceptions.length} item{exceptions.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {exceptions.map(ex => (
                    <div key={ex.id} style={{
                      ...compactRowStyle,
                      borderTop: '1px solid var(--border-color)',
                    }}>
                      <div style={compactRowMainStyle}>
                        <span style={{
                          color: ex.severity === 'error' ? '#991b1b' : '#92400e',
                          fontWeight: 600,
                        }}>
                          {ex.severity === 'error' ? '⛔' : '⚠'} {ex.label}
                        </span>
                        {ex.detail && (
                          <>
                            <span style={compactRowSeparatorStyle}>·</span>
                            <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                              {ex.detail}
                            </span>
                          </>
                        )}
                      </div>
                      {ex.onAction && ex.actionLabel && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={ex.onAction}
                        >
                          {ex.actionLabel} →
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ---- Settings card ---- */}
            <section style={cardStyle}>
              <div style={cardHeaderStyle}>
                <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>FSA Settings</h2>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {/* FSA-2.4 W3 — Create Next Year moved to the
                      engagement-action strip (top of the Workspace).
                      It's an engagement-lifecycle action, not a
                      configuration metadata action — wrong home here. */}
                  {canEdit && (
                    <button className="btn btn-secondary btn-sm" onClick={() => setSettingsModalOpen(true)}>
                      <IconSystem.edit size={14} /> {settings ? 'Edit' : 'Configure FSA'}
                    </button>
                  )}
                </div>
              </div>
              {settingsLoading ? (
                <div style={{ padding: '1.5rem', color: 'var(--text-secondary)' }}>Loading…</div>
              ) : settings ? (
                <div style={settingsGridStyle}>
                  <Field label="Template" value={`${settings.templateCode} · ${settings.templateName}`} />
                  <Field label="Statutory basis" value={settings.templateStatutoryBasis ?? '—'} />
                  <Field label="Entity type" value={settings.templateEntityType ?? '—'} />
                  <Field label="Financial year end" value={formatDate(settings.financialYearEnd)} />
                  <Field label="Period" value={`${settings.periodStart ? formatDate(settings.periodStart) : '—'} → ${settings.periodEnd ? formatDate(settings.periodEnd) : formatDate(settings.financialYearEnd)}`} />
                  <Field label="Currency" value={`${settings.currencyCode ?? '—'}${settings.currencySymbol ? ' (' + settings.currencySymbol + ')' : ''}`} />
                  <Field label="Rounding" value={settings.roundingFactorLabel ?? settings.roundingFactorKey ?? '—'} />
                  <Field label="Carry-forward mode" value={settings.carryForwardMode} />
                  {settings.priorEngagementCode && (
                    <Field label="Prior engagement" value={settings.priorEngagementCode} />
                  )}
                </div>
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  <IconSystem.alert size={24} />
                  <div style={{ marginTop: 8, fontWeight: 500 }}>FSA is not configured for this engagement</div>
                  <div style={{ fontSize: '0.85rem', marginTop: 4 }}>
                    Click <strong>Configure FSA</strong> above to pick a template, currency, rounding and financial-year boundary.
                  </div>
                </div>
              )}
            </section>

            {/* ---- Trial Balance card (only when settings exist) ---- */}
            {settings && (
              <section style={cardStyle}>
                <div style={cardHeaderStyle}>
                  <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Trial Balance</h2>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {/* FSA-2.1.6 — Source TBs registry link. Shows count of live sources. */}
                    {currentTb && (
                      <button
                        type="button"
                        onClick={() => setSourcesModalOpen(true)}
                        style={sourcesLinkStyle}
                        title="Show source TB registry"
                      >
                        <IconSystem.list size={12} /> Source TBs ({currentTb.sourceCount})
                      </button>
                    )}
                    {canEdit && (
                      <button className="btn btn-primary btn-sm" onClick={() => setUploadModalOpen(true)}>
                        <IconSystem.upload size={14} /> Upload New TB
                      </button>
                    )}
                  </div>
                </div>
                {tbLoading ? (
                  <div style={{ padding: '1.5rem', color: 'var(--text-secondary)' }}>Loading…</div>
                ) : currentTb ? (
                  // FSA-2.4 W1 — compact one-line summary. The detailed
                  // breakdown (sources, debit/credit totals, control-
                  // account net) lives in the TB tab's own totals strip
                  // and the Source TBs popup. Workspace only shows what
                  // the auditor needs at a glance: ledger count +
                  // classification %, balance flag, and an exception
                  // chip when something needs attention.
                  <div style={compactRowStyle}>
                    <div style={compactRowMainStyle}>
                      <strong>{currentTb.entryCount}</strong> ledgers
                      <span style={compactRowSeparatorStyle}>·</span>
                      <strong>{currentTb.classifiedCount}</strong> classified
                      ({Math.round((currentTb.classifiedCount / Math.max(1, currentTb.entryCount)) * 100)}%)
                      <span style={compactRowSeparatorStyle}>·</span>
                      {currentTb.sourceCount} source{currentTb.sourceCount === 1 ? '' : 's'}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {Math.abs(currentTb.debitTotal - currentTb.creditTotal) > 0.005 && (
                        <span style={pillStyle('#fee2e2', '#991b1b')} title="Trial balance does not tally">
                          ⚠ Imbalance {formatAmount(currentTb.debitTotal - currentTb.creditTotal)}
                        </span>
                      )}
                      {controlSummary && controlSummary.flaggedCount > 0 && !controlSummary.isBalanced && (
                        <span style={pillStyle('#fee2e2', '#dc2626')} title="Control accounts do not net to zero">
                          ⚠ Control accounts {formatAmount(controlSummary.netTotal)}
                        </span>
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setActiveTab('trial-balance')}
                        title="Open Trial Balance tab"
                      >
                        Open TB →
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    No trial balance uploaded yet.
                  </div>
                )}
              </section>
            )}

            {/* ---- FSA-2.3 First-year Prior-Year TB CTA ----
                 Shown only when:
                   • settings configured (engagement set up)
                   • NO prior engagement linked (first-year / stand-alone)
                   • NO live OPENING source exists yet (idempotent — disappears
                     once the auditor uploads one)
                   • canEdit (read-only viewers don't see the prompt)
                 Tells the auditor that without a prior FSA engagement
                 they need to upload the prior year's PRE-CLOSING TB (BS +
                 P&L) — not just opening balances — to drive PY
                 comparatives. The Opening column on a Current TB only
                 carries BS balances (P&L closed to RE), so it cannot
                 substitute for prior-year P&L comparatives. */}
            {settings
              && !settings.priorEngagementId
              && !hasOpeningSource
              && canEdit && (
              <section style={{ ...cardStyle, borderLeft: '3px solid #d97706' }}>
                <div style={{ padding: '0.75rem 1rem', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ fontSize: 24, lineHeight: 1 }}>📅</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: 4 }}>
                      First-year engagement — upload Prior Year TB for comparatives
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      This engagement has no prior-year FSA engagement linked. Without it, the PY columns on BS / PL / Notes will stay blank.
                      Upload the <strong>prior year's full pre-closing TB</strong> (all ledgers — Balance Sheet AND Profit &amp; Loss) to seed the comparatives.
                      The Opening Balance column on a Current TB carries BS balances only (P&amp;L closed to Retained Earnings) and cannot substitute for prior-year P&amp;L comparatives.
                      Or link a prior engagement in <a href="#" onClick={(e) => { e.preventDefault(); setSettingsModalOpen(true); }} style={{ color: '#1e40af' }}>FSA Settings</a> if one exists.
                    </div>
                  </div>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => { setUploadInitialPurpose('OPENING'); setUploadModalOpen(true); }}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <IconSystem.upload size={14} /> Upload Prior Year TB
                  </button>
                </div>
              </section>
            )}

            {/* ---- FSA-2.3 Opening Balance Reconciliation card ----
                 Hidden when there are no discrepancies (all deltas ≤ 0.01).
                 Only surfaces when prior_engagement_id is linked + a
                 consolidated TB has been uploaded. */}
            {settings?.priorEngagementId && currentTb && (
              <FsaOpeningBalanceReconciliationCard
                engagementId={engagementId}
                refreshKey={currentTb.id ? 1 : 0}
              />
            )}

            {/* ---- Classification status (only when TB present) ----
                 FSA-2.4 W1 — old big-number tile + Open Queue popup
                 removed (workflow now lives in the TB tab via the
                 Unmapped chip + row actions). Kept: progress %, pending
                 class-split count, unmapped count, and a CTA to the TB
                 tab pre-filtered (manual jump for now). */}
            {settings && currentTb && (
              <section style={cardStyle}>
                <div style={cardHeaderStyle}>
                  <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Classification</h2>
                  {coverage && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        ...pillStyle(coverage.percentClassified === 100 ? '#dcfce7' : '#dbeafe',
                                     coverage.percentClassified === 100 ? '#166534' : '#1e40af'),
                      }}>
                        {coverage.classified} / {coverage.total} ({coverage.percentClassified.toFixed(0)}%)
                      </span>
                      {coverage.unclassified > 0 && (
                        <span style={pillStyle('#fef3c7', '#92400e')} title="Ledgers not yet mapped to any Note">
                          {coverage.unclassified} unmapped
                        </span>
                      )}
                      {coverage.scheduleOnly > 0 && (
                        <span style={pillStyle('#fef3c7', '#92400e')} title="Mapped to a Note but leaf class deferred (FSA-2.1.5)">
                          {coverage.scheduleOnly} pending class split
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div style={compactRowStyle}>
                  <div style={compactRowMainStyle}>
                    {coverage && coverage.unclassified === 0 && coverage.scheduleOnly === 0
                      ? <span style={{ color: '#166534', fontWeight: 500 }}>✓ All ledgers classified</span>
                      : <span style={{ color: 'var(--text-secondary)' }}>
                          Resolve unmapped + pending class-split ledgers in the Trial Balance tab.
                        </span>}
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setActiveTab('trial-balance')}
                  >
                    Open TB →
                  </button>
                </div>
              </section>
            )}

            {/* ---- Notes progress strip (FSA-2.4 W2) ----
                 Surfaces lifecycle distribution: finalised vs in-review
                 vs draft vs not-started. Highlights the
                 needing-attention buckets in amber so the auditor can
                 see what's left without opening the Notes tab. CTA jumps
                 to the Notes tab. Only shown after the engagement has
                 TB context (matches the existing "needs a TB first"
                 gating elsewhere). */}
            {settings && currentTb && notesSummary.total > 0 && (
              <section style={cardStyle}>
                <div style={cardHeaderStyle}>
                  <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Notes</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={pillStyle(
                      notesSummary.pctFinalised === 100 ? '#dcfce7' : '#dbeafe',
                      notesSummary.pctFinalised === 100 ? '#166534' : '#1e40af',
                    )}>
                      {notesSummary.finalised} / {notesSummary.total} finalised ({notesSummary.pctFinalised}%)
                    </span>
                    {notesSummary.notStarted > 0 && (
                      <span style={pillStyle('#fef3c7', '#92400e')} title="Notes the auditor has not opened/saved yet">
                        {notesSummary.notStarted} not started
                      </span>
                    )}
                  </div>
                </div>
                <div style={compactRowStyle}>
                  <div style={compactRowMainStyle}>
                    {notesSummary.inReview > 0 && (
                      <><strong>{notesSummary.inReview}</strong> in review<span style={compactRowSeparatorStyle}>·</span></>
                    )}
                    {notesSummary.draft > 0 && (
                      <><strong>{notesSummary.draft}</strong> draft<span style={compactRowSeparatorStyle}>·</span></>
                    )}
                    {notesSummary.notStarted > 0 && (
                      <><strong>{notesSummary.notStarted}</strong> not started<span style={compactRowSeparatorStyle}>·</span></>
                    )}
                    {notesSummary.excluded > 0 && (
                      <span style={{ color: 'var(--text-secondary)' }}>
                        {notesSummary.excluded} excluded
                      </span>
                    )}
                    {notesSummary.inReview === 0 && notesSummary.draft === 0 && notesSummary.notStarted === 0 && (
                      <span style={{ color: '#166534', fontWeight: 500 }}>✓ All notes finalised</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setActiveTab('notes')}
                  >
                    Open Notes →
                  </button>
                </div>
              </section>
            )}

            {/* ---- Statements progress strip (FSA-2.4 W2) ----
                 One row per FINANCIAL_STATEMENT report (BS / PL / CF).
                 Each row shows save state + last refreshed + excluded
                 flag. BS drift / cascade-staleness will fold into W3
                 (exception strip) once those signals are wired. */}
            {settings && currentTb && statementsSummary.length > 0 && (
              <section style={cardStyle}>
                <div style={cardHeaderStyle}>
                  <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Financial Statements</h2>
                  <span style={pillStyle('#dbeafe', '#1e40af')}>
                    {statementsSummary.filter(s => s.hasSavedData && !s.isExcluded).length} / {statementsSummary.filter(s => !s.isExcluded).length} generated
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {statementsSummary.map(s => {
                    const isReady = s.hasSavedData && !s.isExcluded;
                    const lastRefreshed = s.updatedAt
                      ? new Date(s.updatedAt).toLocaleString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })
                      : null;
                    // FSA-2.4 W2 fix — tab IDs are lowercase short codes
                    // ('bs' / 'pl' / 'cf'), matching WorkspaceTab type
                    // + the left-rail item ids. Earlier values
                    // ('balance-sheet' etc.) were wrong → setActiveTab
                    // silently no-op'd. Codes from the DB match these.
                    const tabKey: WorkspaceTab | null =
                      s.reportCode === 'BS' ? 'bs'
                      : s.reportCode === 'PL' ? 'pl'
                      : s.reportCode === 'CF' ? 'cf'
                      : null;
                    return (
                      <div key={s.reportId} style={{
                        ...compactRowStyle,
                        borderTop: '1px solid var(--border-color)',
                      }}>
                        <div style={compactRowMainStyle}>
                          <strong style={{ minWidth: 130 }}>{s.reportName}</strong>
                          {s.isExcluded ? (
                            <span style={pillStyle('#e5e7eb', '#374151')} title="Marked Not Applicable for this engagement">
                              Not applicable
                            </span>
                          ) : isReady ? (
                            <>
                              {/* FSA-2.4 — BS drift chip. Only meaningful on
                                  the BS row (bsDriftAmount NULL elsewhere).
                                  Threshold matches the BS screen chip
                                  (0.5 paisa = 0.005 rupees). Red chip when
                                  drift > tolerance, green ✓ when balanced. */}
                              {typeof s.bsDriftAmount === 'number' && s.bsDriftAmount > 0.005 ? (
                                <span style={pillStyle('#fee2e2', '#991b1b')} title="Total Assets does not equal Total Equity + Liabilities">
                                  ⚠ Drift {formatAmount(s.bsDriftAmount)}
                                </span>
                              ) : (
                                <span style={pillStyle('#dcfce7', '#166534')}>
                                  {typeof s.bsDriftAmount === 'number' ? '✓ Reconciled' : '✓ Generated'}
                                </span>
                              )}
                              {lastRefreshed && (
                                <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                                  · last refreshed {lastRefreshed}
                                </span>
                              )}
                            </>
                          ) : (
                            <span style={pillStyle('#fef3c7', '#92400e')}>Not yet generated</span>
                          )}
                        </div>
                        {tabKey && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setActiveTab(tabKey)}
                          >
                            Open {s.reportCode} →
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Trial Balance tab — persistent once visited. display:none toggle
            keeps the component instance (and its Edit Classification modal +
            minimize closures) alive across left-rail navigation. */}
        {tbTabMounted && (
          <div style={{
            flex: 1,
            overflow: 'hidden',
            display: activeTab === 'trial-balance' ? 'flex' : 'none',
            flexDirection: 'column',
            minHeight: 0,
          }}>
            <FsaTrialBalanceTab
              trialBalanceId={currentTb?.id ?? null}
              templateId={settings?.templateId ?? null}
              engagementId={engagementId ?? null}
              journalRefreshSignal={journalRefreshSignal}
              tabId={tabId}
              moduleName={moduleName}
            />
          </div>
        )}

        {activeTab === 'adjustments' && engagementId && (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <FsaAdjustmentsTab
              engagementId={engagementId}
              tabId={tabId}
              moduleName={moduleName}
              onJournalChanged={bumpJournalRefresh}
            />
          </div>
        )}

        {activeTab === 'notes' && engagementId && (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <FsaNotesTab
              engagementId={engagementId}
              trialBalanceId={currentTb?.id ?? null}
              tabId={tabId}
              moduleName={moduleName}
            />
          </div>
        )}

        {/* FSA-2.2 Wave 3 Statement save — BS / PL / CF tabs open the
            shared FsaStatementScreen. Open-Note from a NOTE_TOTAL row's
            action menu opens the Note Modal IN PLACE above the
            Statement (managed inside FsaStatementScreen) — auditor
            stays on the Statement when the modal closes. */}
        {(activeTab === 'bs' || activeTab === 'pl' || activeTab === 'cf') && engagementId && (
          <StatementsTabContent
            engagementId={engagementId}
            reportCode={tabToReportCode(activeTab)}
            tabId={tabId}
            moduleName={moduleName}
          />
        )}

        {activeTab === 'other' && engagementId && (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <FsaReportsTab
              engagementId={engagementId}
              tabId={tabId}
              moduleName={moduleName}
            />
          </div>
        )}

        {activeTab === 'print-fs' && engagementId && (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <FsaConsolidatedPrintScreen engagementId={engagementId} />
          </div>
        )}

        {activeTab === 'signature-panel' && engagementId && (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <FsaSignaturePanelPane
              engagementId={String(engagementId)}
              clientId={settings?.clientId ? String(settings.clientId) : null}
              templateCode={settings?.templateCode ?? null}
            />
          </div>
        )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {engagementId && (
        <FsaEngagementSettingsModal
          isOpen={settingsModalOpen}
          mode={settings ? 'edit' : 'create'}
          engagementId={engagementId}
          existing={settings ?? null}
          onClose={() => setSettingsModalOpen(false)}
          onSaved={() => {
            setSettingsModalOpen(false);
            refetchSettings();
            notificationService.toast.success('FSA settings saved.');
          }}
        />
      )}
      {/* FSA-2.3 Roll Forward — confirm dialog. Pre-fills from THIS
          engagement (treated as the "prior" for next-year creation). */}
      {engagementId && settings && (
        <FsaRollForwardConfirmModal
          isOpen={rollForwardModalOpen}
          priorEngagementId={engagementId}
          priorEngagementCode={settings.engagementCode ?? null}
          priorClientName={settings.clientName ?? null}
          onClose={() => setRollForwardModalOpen(false)}
          onCreated={(result) => {
            // Open the newly-created FSA workspace in a new tab so the
            // auditor lands directly on it. Same pattern as the FSA
            // Engagements list "Open" row action.
            const clientLabel = settings.clientName?.trim() || 'Engagement';
            const tabTitle = `${clientLabel} (next year) · FSA`;
            openModule(
              'fsa-engagement',
              { engagementId: result.newEngagementId },
              { instanceKey: String(result.newEngagementId), tabTitle },
            );
          }}
          onOpenExisting={(existingEngagementId) => {
            // Real-duplicate case: user clicks "Open existing instead" on
            // the warning banner. Navigate to that engagement's workspace.
            const clientLabel = settings.clientName?.trim() || 'Engagement';
            const tabTitle = `${clientLabel} · FSA`;
            openModule(
              'fsa-engagement',
              { engagementId: existingEngagementId },
              { instanceKey: String(existingEngagementId), tabTitle },
            );
          }}
        />
      )}
      {engagementId && settings && (
        <TrialBalanceUploadModal
          isOpen={uploadModalOpen}
          engagementId={engagementId}
          initialPurpose={uploadInitialPurpose}
          onClose={() => { setUploadModalOpen(false); setUploadInitialPurpose('CURRENT'); }}
          onUploaded={() => {
            setUploadModalOpen(false);
            setUploadInitialPurpose('CURRENT');
            refetchTb();
            refetchCoverage();
            refetchControlSummary();
            refetchHasOpening();
            notificationService.toast.success('Trial balance uploaded.');
          }}
        />
      )}
      {/* FSA-2.4 W1 — FsaUnmappedLedgerPopup render removed. */}
      {/* FSA-2.1.6 — Source TBs registry panel. */}
      {settings && engagementId && (
        <SourceTbRegistryModal
          isOpen={sourcesModalOpen}
          engagementId={engagementId}
          canEdit={canEdit}
          onClose={() => setSourcesModalOpen(false)}
          onChanged={() => { refetchTb(); refetchCoverage(); refetchControlSummary(); }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Statement tab content — resolves the WorkspaceTab id ('bs' | 'pl' | 'cf')
// to a backend report code via the Statements list, then renders the
// shared FsaStatementScreen. Lives inline here so it can read the
// engagement context without prop-drilling and stays tightly coupled to
// the workspace tab definition above.
// ---------------------------------------------------------------------------

function tabToReportCode(tab: WorkspaceTab): string {
  if (tab === 'bs') return 'BS';
  if (tab === 'pl') return 'PL';
  if (tab === 'cf') return 'CF';
  return '';
}

const StatementsTabContent: React.FC<{
  engagementId: ID;
  reportCode: string;
  tabId?: string;
  moduleName?: string;
}> = ({ engagementId, reportCode, tabId, moduleName }) => {
  const { data: list, loading } = useFsaStatementList(engagementId);
  const item = useMemo(() => list.find(s => s.reportCode === reportCode), [list, reportCode]);
  if (loading && !item) {
    return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>Loading Statements…</div>;
  }
  if (!item) {
    return (
      <div style={placeholderStyle}>
        <IconSystem.chart size={36} />
        <div style={{ fontWeight: 600 }}>Statement not configured</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          The engagement&apos;s template has no <code>{reportCode}</code> report.
        </div>
      </div>
    );
  }
  return (
    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <FsaStatementScreen
        engagementId={engagementId}
        reportId={item.reportId}
        reportCode={item.reportCode}
        reportName={item.reportName}
        tabId={tabId}
        moduleName={moduleName}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Inline helpers
// ---------------------------------------------------------------------------

// Workspace navigation — grouped left-rail items. Each item resolves to one
// of the WorkspaceTab IDs already used for content gating. Icon refs are
// looked up against IconSystem at render time.
const WORKSPACE_GROUPS: Array<{
  label: string;
  items: Array<{ id: WorkspaceTab; label: string; iconKey: string }>;
}> = [
  { label: 'WORKSPACE', items: [
    { id: 'general',       label: 'General / Basic', iconKey: 'settings' },
    { id: 'trial-balance', label: 'Trial Balance',   iconKey: 'list' },
    { id: 'adjustments',   label: 'Adjustments',     iconKey: 'edit' },
  ]},
  { label: 'STATEMENTS', items: [
    { id: 'bs',      label: 'Balance Sheet',  iconKey: 'chart' },
    { id: 'pl',      label: 'Profit & Loss',  iconKey: 'dollarSign' },
    { id: 'cf',      label: 'Cash Flow',      iconKey: 'refresh' },
  ]},
  { label: 'REPORTS', items: [
    { id: 'notes',    label: 'Notes',                  iconKey: 'list' },
    { id: 'other',    label: 'Other Reports',          iconKey: 'file' },
    { id: 'print-fs', label: 'Financial Statements',   iconKey: 'print' },
  ]},
  { label: 'SIGN-OFF', items: [
    { id: 'signature-panel', label: 'Signature Panel', iconKey: 'edit' },
  ]},
];

const RAIL_COLLAPSE_PREF = 'fsa-workspace-rail-collapsed';

// Render an item icon. Falls back to `chart` if the requested key isn't on
// IconSystem (defensive — keeps the rail useful even if an icon is renamed).
const RailIcon: React.FC<{ iconKey: string; size?: number }> = ({ iconKey, size = 16 }) => {
  const Cmp = (IconSystem as any)[iconKey] ?? IconSystem.chart;
  return <Cmp size={size} />;
};

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-secondary)', letterSpacing: 0.4 }}>{label}</div>
    <div style={{ fontSize: '0.85rem', marginTop: 2, color: 'var(--text-primary)' }}>{value}</div>
  </div>
);

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const formatDateTime = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const formatAmount = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Workspace shell — left rail + content panel
// ---------------------------------------------------------------------------
const workspaceShellStyle: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
};
const railBaseStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg-secondary)',
  borderRight: '1px solid var(--border-color)',
  transition: 'width 0.18s ease',
  flexShrink: 0,
  overflow: 'hidden',
};
const railExpandedStyle: React.CSSProperties = {
  ...railBaseStyle,
  width: 220,
};
const railCollapsedStyle: React.CSSProperties = {
  ...railBaseStyle,
  width: 52,
};
const railHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.5rem 0.5rem 0.5rem 0.75rem',
  borderBottom: '1px solid var(--border-color)',
  minHeight: 36,
};
const railHeaderTitleStyle: React.CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: 0.6,
  color: 'var(--text-secondary)',
};
const railToggleBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--form-border-color)',
  borderRadius: 4,
  width: 24,
  height: 24,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: 'var(--text-secondary)',
};
const railNavStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '0.5rem 0',
};
const railGroupStyle: React.CSSProperties = {
  marginBottom: '0.5rem',
};
const railGroupHeaderStyle: React.CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 700,
  letterSpacing: 0.7,
  color: 'var(--text-muted, #94a3b8)',
  padding: '0.35rem 0.75rem 0.25rem',
  textTransform: 'uppercase',
};
const railItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '0.5rem 0.75rem',
  background: 'transparent',
  border: 'none',
  borderLeft: '3px solid transparent',
  fontSize: '0.85rem',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  textAlign: 'left',
};
const railItemCollapsedStyle: React.CSSProperties = {
  justifyContent: 'center',
  padding: '0.5rem',
  gap: 0,
};
const railItemActiveStyle: React.CSSProperties = {
  background: 'var(--bg-primary)',
  borderLeftColor: '#2563eb',
  fontWeight: 600,
};
const railItemIconStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 16,
  height: 16,
  color: 'var(--text-secondary)',
};
const railItemLabelStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// FSA-2.4 UI polish — rail tooltip. Position is set inline (top/left)
// from the trigger's getBoundingClientRect. Vertical-center using
// translateY(-50%). High z-index keeps it above the rail / sticky
// elements. Theme-aware via CSS vars; arrow on the left points back to
// the icon.
const railTooltipStyle: React.CSSProperties = {
  position: 'fixed',
  zIndex: 9999,
  transform: 'translateY(-50%)',
  background: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-color)',
  borderRadius: 6,
  padding: '6px 10px',
  fontSize: '0.78rem',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
  minWidth: 120,
};
const railTooltipGroupStyle: React.CSSProperties = {
  fontSize: '0.62rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-secondary)',
  marginBottom: 2,
};
const railTooltipLabelStyle: React.CSSProperties = {
  fontWeight: 600,
  color: 'var(--text-primary)',
};
const contentPanelStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
};
const cardStyle: React.CSSProperties = {
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
  borderRadius: 8,
  marginBottom: '1rem',
  overflow: 'hidden',
};
const cardHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.75rem 1rem',
  background: 'var(--bg-secondary)',
  borderBottom: '1px solid var(--border-color)',
};
const settingsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '1rem',
  padding: '1rem',
};
const pillStyle = (bg: string, fg: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  background: bg,
  color: fg,
  fontSize: '0.7rem',
  fontWeight: 600,
});
const badgeStripStyle: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  flexShrink: 0,
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
};

// FSA-2.4 W1+ — header mini-status strip chips. Pill shape, button
// behaviour (clickable → jump to relevant sub-tab), color-coded per
// progress band. Matches the pillStyle dimensions for visual
// continuity with the rest of the workspace.
const headerChipButtonStyle = (bg: string, fg: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 999,
  border: 'none',
  background: bg,
  color: fg,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.72rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
});

// FSA-2.4 W1 — single-row compact-summary layout used by TB / Classification
// cards (replaces the 2-line stacked card content). Left side = main
// summary line (text); right side = action/exception chips + CTA.
const compactRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '0.6rem 1rem',
  flexWrap: 'wrap',
};
const compactRowMainStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  color: 'var(--text-primary)',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flexWrap: 'wrap',
};
const compactRowSeparatorStyle: React.CSSProperties = {
  color: 'var(--text-secondary)',
  margin: '0 4px',
};
// Single-line inline context row (Row 2 of the 2-row header). Uses
// white-space + text-overflow so a long description ellipsises instead of
// wrapping to a third line.
const contextLineStyle: React.CSSProperties = {
  display: 'block',
  marginTop: 2,
  fontSize: '0.8125rem',
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // Preserve the gap-rendering between adjacent <span>s when on one line.
  // (gap: 6 doesn't apply on block; we use inline separator chars instead.)
};
const contextSeparatorStyle: React.CSSProperties = {
  color: 'var(--text-muted, #94a3b8)',
  margin: '0 6px',
};

/** Build a `title` attribute for the context line so the truncated text is recoverable on hover. */
const contextTitleText = (
  settings: { engagementTypeName?: string | null; engagementCode?: string | null; engagementDescription?: string | null } | null | undefined
): string => {
  if (!settings) return 'FSA Workspace';
  const parts = ['FSA Workspace'];
  if (settings.engagementTypeName) parts.push(settings.engagementTypeName);
  if (settings.engagementCode) parts.push(settings.engagementCode);
  if (settings.engagementDescription) parts.push(settings.engagementDescription);
  return parts.join(' · ');
};
const emptyStateStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: '3rem 1rem',
  minHeight: '50vh',
};
const placeholderStyle: React.CSSProperties = {
  ...emptyStateStyle,
  flex: 1,
};
const summaryTileWrapStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '1rem 1.25rem',
  margin: '0.5rem',
  borderRadius: 8,
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
};
const summaryTileLeftStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
};
const summaryTileBigStyle: React.CSSProperties = {
  fontSize: '1.75rem',
  fontWeight: 700,
  lineHeight: 1,
  color: '#92400e',
  fontVariantNumeric: 'tabular-nums',
  minWidth: 32,
  textAlign: 'right',
};
const summaryTileLabelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  fontSize: '0.85rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
};
const summaryTileSubStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 500,
  color: 'var(--text-secondary)',
};
// FSA-2.1.6 — small "Source TBs (N)" link in the TB card header.
const sourcesLinkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 8px',
  borderRadius: 4,
  background: 'transparent',
  border: '1px solid var(--form-border-color)',
  fontSize: '0.75rem',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
};

export default FsaEngagementScreen;
